'use client';

import { CustomButton } from '@/components/common';
import { getRoadMapsById, getRoadmapsTypeStatusesByRoadmapId } from '@/domain/roadmap/api';
import { type RoadmapStatus } from '@/domain/roadmap/types';
import { useAuth } from '@/hooks/useAuth';
import { canEditRoadmap, type GenericRoadmapWithProduct } from '@/utils/roadmapPermission';
import { CircularProgress } from '@andritzot/metris-web-ui/feedback/progress/circular-progress';
import EditOutlined from '@mui/icons-material/EditOutlined';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { useRoadmapEditStore } from '../../store/roadmap';
import { getImpact } from '../../utils';

const RoadMapEditDialog = dynamic(() => import('../RoadMapEditDialog/RoadMapEditDialog'));

interface EditRoadmapButtonProps {
  roadmap: GenericRoadmapWithProduct;
  onSaveCallback?: () => void;
}

const EditRoadmapButton = ({ roadmap, onSaveCallback }: EditRoadmapButtonProps) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { user } = useAuth();
  const disabled = !canEditRoadmap(user, roadmap);

  const getRoadmapsTypeStatusesByRoadmapIdAsync = async (
    roadmapId: number,
    typeId: number,
  ): Promise<RoadmapStatus[]> => {
    try {
      return await getRoadmapsTypeStatusesByRoadmapId(roadmapId, typeId);
    } catch (e) {
      toast.error(`Error while getting roadmaps type statuses ${e}`);
      throw e;
    }
  };

  const onHide = () => {
    setShowEditDialog(false);
  };

  const editItem = async () => {
    setIsFetching(true);
    try {
      const editableRoadmap = await getRoadMapsById(roadmap.idRoadmap);
      let statuses = [] as RoadmapStatus[];
      if (editableRoadmap.type?.idRoadmapType) {
        statuses = await getRoadmapsTypeStatusesByRoadmapIdAsync(
          editableRoadmap.idRoadmap,
          editableRoadmap.type.idRoadmapType,
        );
      }
      useRoadmapEditStore.setState({
        editableRoadMap: {
          ...editableRoadmap,
          owner: editableRoadmap.owner ?? null,
          description: editableRoadmap.description ?? '',
          detailedDescription: editableRoadmap.detailedDescription ?? '',
          item: editableRoadmap.item ?? '',
          qualityCertificate: editableRoadmap.qualityCertificate ?? '',
          specification: editableRoadmap.specification ?? '',
          milestones: editableRoadmap.milestones ?? [],
          plants: editableRoadmap.plants?.map((plant) => plant.idPlant) ?? [],
          okrs:
            editableRoadmap.okrs?.map((okr) => ({
              id: okr.id,
              impact: { id: okr.impact, impact: getImpact(okr.impact) },
            })) ?? [],
          unitsImpact:
            editableRoadmap.unitsImpact?.map((u) => ({
              unitId: u.unitId,
              isRequested: u.isRequested,
            })) ?? [],
          linkedItems: editableRoadmap.linkedItems ?? [],
          type: editableRoadmap.type ? { ...editableRoadmap.type, roadmapStatus: statuses } : null,
          version: editableRoadmap.version ?? null,
        },
      });
      setShowEditDialog(true);
    } catch (e) {
      toast.error(`Error while getting roadmaps ${e}`);
      throw e;
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <>
      <CustomButton.Root
        startIcon={isFetching ? <CircularProgress size={16} /> : <EditOutlined />}
        disabled={disabled || isFetching}
        color="success"
        sx={{ borderRadius: '50px', mr: 1 }}
        onClick={editItem}
      >
        <CustomButton.Tooltip disabled={!disabled} mouseTrack>
          <div>
            <p>Editing requires one the following permissions:</p>
            <ul>
              <li>
                <b>Admin</b>
              </li>
              <li>
                <b>Roadmap owner</b>
              </li>
              <li>
                <b>Technical Product Owner</b>
              </li>
              <li>
                <b>Global Product manager</b>
              </li>
              <li>
                <b>Head of Technology</b>
              </li>
              <li>
                <b>QA specialist</b>
              </li>
            </ul>
          </div>
        </CustomButton.Tooltip>
      </CustomButton.Root>
      {showEditDialog && (
        <RoadMapEditDialog viewOnly={false} visible setVisible={onHide} onSaveCallback={onSaveCallback} />
      )}
    </>
  );
};

export default EditRoadmapButton;
